"use client"

import { useEffect, useState } from "react"
import axios from "../api/axios"
import { toast } from "react-toastify"
import { Shield, Users, Settings, Save, Search, Filter } from "lucide-react"

const PermissionsPanel = () => {
  const [roles, setRoles] = useState([])
  const [modules, setModules] = useState([])
  const [permissions, setPermissions] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedRole, setSelectedRole] = useState("all")
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    axios
      .get("/api/permissions")
      .then(({ data }) => {
        setRoles(data.roles)
        setModules(data.modules)
        setPermissions(data.permissions)
      })
      .catch((err) => {
        console.error(err)
        toast.error("Failed to load data.")
      })
  }, [])

  const hasPermission = (moduleId, roleId) => {
    return permissions.some((p) => p.module_id === moduleId && p.role_id === roleId && !!p.can_view)
  }

  const togglePermission = (moduleId, roleId) => {
    setPermissions((prev) => {
      const index = prev.findIndex((p) => p.module_id === moduleId && p.role_id === roleId)

      if (index >= 0) {
        const updated = [...prev]
        updated[index] = {
          ...updated[index],
          can_view: !updated[index].can_view,
        }
        return updated
      } else {
        return [...prev, { module_id: moduleId, role_id: roleId, can_view: true }]
      }
    })
  }

  const savePermissions = async () => {
    setIsLoading(true)
    try {
      await axios.post("/api/permissions", { permissions })
      toast.success("Permissions saved successfully!")
    } catch (err) {
      console.error(err)
      toast.error("Error saving permissions.")
    } finally {
      setIsLoading(false)
    }
  }

  const filteredModules = modules.filter((module) => module.name.toLowerCase().includes(searchTerm.toLowerCase()))

  const filteredRoles =
    selectedRole === "all" ? roles : roles.filter((role) => role.role_id.toString() === selectedRole)

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <Shield size={24} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Permissions Management</h1>
              <p className="text-gray-600">Manage user roles and module access permissions</p>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search modules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="md:w-48">
              <div className="relative">
                <Filter size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none"
                >
                  <option value="all">All Roles</option>
                  {roles.map((role) => (
                    <option key={role.role_id} value={role.role_id.toString()}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Permissions Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-orange-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Users size={20} className="text-orange-600" />
                <h2 className="text-xl font-semibold text-gray-900">Role Permissions Matrix</h2>
              </div>
              <div className="text-sm text-orange-700 font-medium">
                {filteredModules.length} modules • {filteredRoles.length} roles
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 min-w-[200px]">Module Name</th>
                  {filteredRoles.map((role) => (
                    <th
                      key={role.role_id}
                      className="px-4 py-4 text-center text-sm font-semibold text-gray-900 min-w-[120px]"
                    >
                      <div className="flex flex-col items-center space-y-1">
                        <span>{role.role_name}</span>
                        <span className="text-xs text-gray-500 font-normal">Role ID: {role.role_id}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredModules.map((module, index) => (
                  <tr
                    key={module.id}
                    className={`hover:bg-orange-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-25"}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <Settings size={16} className="text-orange-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{module.name}</div>
                          <div className="text-xs text-gray-500">Module ID: {module.id}</div>
                        </div>
                      </div>
                    </td>
                    {filteredRoles.map((role) => (
                      <td key={`${module.id}-${role.role_id}`} className="px-4 py-4 text-center">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={hasPermission(module.id, role.role_id)}
                            onChange={() => togglePermission(module.id, role.role_id)}
                            className="sr-only"
                          />
                          <div
                            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                              hasPermission(module.id, role.role_id) ? "bg-orange-500" : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 shadow-sm ${
                                hasPermission(module.id, role.role_id) ? "transform translate-x-6" : ""
                              }`}
                            ></div>
                          </div>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredModules.length === 0 && (
            <div className="p-12 text-center">
              <Settings size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No modules found</h3>
              <p className="text-gray-500">Try adjusting your search criteria</p>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={savePermissions}
            disabled={isLoading}
            className="flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Save size={20} />
            )}
            <span>{isLoading ? "Saving..." : "Save Permissions"}</span>
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-orange-100 rounded-xl">
                <Users size={24} className="text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{roles.length}</p>
                <p className="text-sm text-gray-600">Total Roles</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <Settings size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{modules.length}</p>
                <p className="text-sm text-gray-600">Total Modules</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Shield size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{permissions.filter((p) => p.can_view).length}</p>
                <p className="text-sm text-gray-600">Active Permissions</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PermissionsPanel
